/**
 * Motivos de pausa (spec 015, FR-003) — mesmo desenho dos motivos de
 * encerramento da 013. O atendente escolhe um destes no chip de status ao
 * entrar em pausa; o relatório de atendentes quebra o tempo de pausa por eles.
 * Desativar tira da escolha sem apagar das pausas antigas; excluir só vale
 * para motivo nunca usado (a API responde 409 e a tela sugere desativar).
 * Escrita é do admin — para os demais a lista aparece só para leitura.
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Check, Pencil, Trash2, X } from 'lucide-react';
import { PAUSE_REASON_NAME_MAX, type PauseReason } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Spinner } from '@/components/ui/misc';
import { pauseReasonsQuery, presenceErrorMessage } from './presence-queries';
import {
  useCreatePauseReason,
  useDeletePauseReason,
  useReorderPauseReasons,
  useUpdatePauseReason,
} from './presence-mutations';


export function PauseReasonsSection({ canEdit }: { canEdit: boolean }) {
  const reasonsQ = useQuery(pauseReasonsQuery());
  const create = useCreatePauseReason();
  const reorder = useReorderPauseReasons();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reasons = reasonsQ.data ?? [];

  const add = (e: FormEvent) => {
    e.preventDefault();
    const value = name.trim();
    if (!value) return;
    setError(null);
    create.mutate(
      { name: value, active: true },
      { onSuccess: () => setName(''), onError: (err) => setError(presenceErrorMessage(err)) }
    );
  };

  const move = (index: number, delta: -1 | 1) => {
    const ids = reasons.map((r) => r.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target]!, ids[index]!];
    setError(null);
    reorder.mutate(ids, { onError: (err) => setError(presenceErrorMessage(err)) });
  };

  return (
    <section className="rounded-lg border shadow-xs">
      <div className="border-b px-4 py-3">
        <h2 className="text-sm font-semibold">Motivos de pausa</h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          O atendente escolhe um destes ao entrar em pausa. Motivo desativado some da escolha, mas
          continua nas pausas antigas e no relatório.
        </p>
      </div>

      <div className="space-y-3 p-4">
        {reasonsQ.isPending ? (
          <Spinner className="size-5" />
        ) : reasonsQ.isError ? (
          <p className="text-sm text-destructive">{presenceErrorMessage(reasonsQ.error)}</p>
        ) : reasons.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum motivo cadastrado.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {reasons.map((r, i) => (
              <PauseReasonRow
                key={r.id}
                reason={r}
                canEdit={canEdit}
                first={i === 0}
                last={i === reasons.length - 1}
                reordering={reorder.isPending}
                onMove={(delta) => move(i, delta)}
                onError={setError}
              />
            ))}
          </ul>
        )}

        {canEdit ? (
        <form onSubmit={add} className="flex items-center gap-2">
          <Input
            aria-label="Novo motivo"
            className="h-8"
            value={name}
            maxLength={PAUSE_REASON_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            placeholder="Novo motivo (ex.: Banheiro)"
          />
          <Button type="submit" size="sm" disabled={create.isPending || !name.trim()}>
            Adicionar
          </Button>
        </form>
        ) : null}

        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </section>
  );
}

function PauseReasonRow({
  reason,
  canEdit,
  first,
  last,
  reordering,
  onMove,
  onError,
}: {
  reason: PauseReason;
  canEdit: boolean;
  first: boolean;
  last: boolean;
  reordering: boolean;
  onMove: (delta: -1 | 1) => void;
  onError: (message: string | null) => void;
}) {
  const update = useUpdatePauseReason();
  const remove = useDeletePauseReason();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(reason.name);

  const save = () => {
    const value = name.trim();
    if (!value || value === reason.name) {
      setEditing(false);
      setName(reason.name);
      return;
    }
    onError(null);
    update.mutate(
      { id: reason.id, name: value },
      { onSuccess: () => setEditing(false), onError: (err) => onError(presenceErrorMessage(err)) }
    );
  };

  if (!canEdit) {
    return (
      <li className="flex items-center gap-2 px-3 py-2">
        <span className={`min-w-0 flex-1 truncate text-sm ${reason.active ? '' : 'text-muted-foreground line-through'}`}>
          {reason.name}
        </span>
        {reason.active ? null : <span className="text-xs text-muted-foreground">desativado</span>}
      </li>
    );
  }

  return (
    <li className="flex items-center gap-2 px-3 py-2">
      <div className="flex shrink-0 flex-col">
        <button
          type="button"
          aria-label={`Subir ${reason.name}`}
          disabled={first || reordering}
          onClick={() => onMove(-1)}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
        >
          <ArrowUp className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label={`Descer ${reason.name}`}
          disabled={last || reordering}
          onClick={() => onMove(1)}
          className="text-muted-foreground hover:text-foreground disabled:opacity-30"
        >
          <ArrowDown className="size-3.5" />
        </button>
      </div>

      {editing ? (
        <form
          className="flex min-w-0 flex-1 items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <Input
            autoFocus
            aria-label="Nome do motivo"
            className="h-7"
            value={name}
            maxLength={PAUSE_REASON_NAME_MAX}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" variant="ghost" size="icon-sm" aria-label="Salvar nome">
            <Check />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Cancelar edição"
            onClick={() => {
              setEditing(false);
              setName(reason.name);
            }}
          >
            <X />
          </Button>
        </form>
      ) : (
        <span className={`min-w-0 flex-1 truncate text-sm ${reason.active ? '' : 'text-muted-foreground line-through'}`}>
          {reason.name}
        </span>
      )}

      <Switch
        aria-label={reason.active ? `Desativar ${reason.name}` : `Ativar ${reason.name}`}
        checked={reason.active}
        disabled={update.isPending}
        onCheckedChange={(active) =>
          update.mutate({ id: reason.id, active }, { onError: (err) => onError(presenceErrorMessage(err)) })
        }
      />
      {!editing ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Renomear ${reason.name}`}
          onClick={() => setEditing(true)}
        >
          <Pencil />
        </Button>
      ) : null}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Excluir ${reason.name}`}
        disabled={remove.isPending}
        onClick={() => {
          onError(null);
          remove.mutate(reason.id, { onError: (err) => onError(presenceErrorMessage(err)) });
        }}
      >
        <Trash2 />
      </Button>
    </li>
  );
}
